import { QueryFactory, isAxiosError } from '@lmt/shared-types';
import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import {
  NetworkError,
  ReactRouter404,
} from '@lmt/unicorn-shared/app/helpers/error-handling';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import {
  useQueryClient,
  useQueryErrorResetBoundary,
} from '@tanstack/react-query';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../components/common/Loading.module.scss';
import { logOut } from '../features/auth/operations';
import { KnownError } from './KnownError';
import { isElectron } from './common';
import { useLocation } from 'react-router';

type ErrorBoundaryFallbackProps = {
  error: Error;
  componentStack: string | null;
  resetError(): void;
};
export const errorBoundaryFallbackFunction = (
  props: ErrorBoundaryFallbackProps,
) => <ErrorBoundaryFallback {...props} />;
export const ErrorBoundaryFallback = (props: ErrorBoundaryFallbackProps) => {
  const location = window.location.href;
  const queryClient = useQueryClient();
  const i18n = useTranslation();
  const loc = useLocation();
  const [initialLocation] = useState(window.location.href);
  useEffect(() => {
    if (initialLocation !== location) {
      props.resetError();
    }
  }, [initialLocation, location, loc.pathname]);
  const errorString = convertToErrorString(props.error);

  const versionQuery = QueryFactory.VersionQuery.useVersionQuery({
    throwOnError: false,
  });
  const isServerAvailable = !!versionQuery.data;

  // if error is NetworkError, but server is available, there's a high chance we are trying to request an old chunk of JS or CSS
  // (unfortunately it's impossible to get the error code of Network response and compare it with 404).
  // so we show proper error message and offer to reload the whole page
  const isServerUpdated =
    errorString === NetworkError &&
    !isAxiosError(props.error) &&
    isServerAvailable;

  const isClientSide404 =
    ('status' in props.error && props.error.status == 404) ||
    errorString === ReactRouter404;
  const queryErrorReset = useQueryErrorResetBoundary();
  const isLogOutShown =
    props.error instanceof KnownError ? props.error.showLogOut : true;
  return (
    <div className={styles.flexContainer}>
      <div className={styles.flexLoadingData} data-test-id="loading-error">
        <div className={styles.loading}>
          <h1>
            {isClientSide404 ? i18n.t('load.client_side_404') : errorString}
          </h1>
          {isClientSide404 ? (
            <>
              <Button
                onClick={async () => {
                  if (isElectron()) {
                    document.location.hash = '#/';
                    document.location.reload();
                  } else {
                    document.location = '/';
                  }
                }}
                title={i18n.t('load.go_to_index')}
              />
            </>
          ) : isServerUpdated ? (
            <>
              <div className={styles.serverUpdated}>
                {i18n.t('load.server_updated')}
              </div>
              <Button
                onClick={async () => {
                  document.location.reload();
                }}
                title={i18n.t('load.reload_page')}
              />
            </>
          ) : (
            <div className={styles.errorContainerButtons}>
              <Button
                onClick={async () => {
                  //Bug 40811: Page is not loaded with "Running" backend after clicking "Reload" button
                  void queryClient.refetchQueries({
                    predicate: (x) => !!x.state.error,
                  });
                  queryErrorReset.reset();
                  props.resetError();
                }}
                title={i18n.t('load.reload')}
              />
              {isLogOutShown && (
                <>
                  <div className={styles.errorContainerButtonsOr}>
                    {i18n.t('load.or')}
                  </div>
                  <Button
                    onClick={async () => {
                      logOut();
                      //navigate(pages.home.link(), { replace: true });
                    }}
                    title={i18n.t('load.log_out')}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
